import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useFormState } from 'react-final-form';
import { FormInput } from '../../../../../../components/atoms/Fields/Input';
import { FormSimpleSelect } from '../../../../../../components/atoms/Fields/Select/templates/SimpleSelect';
import Button from '../../../../../../components/atoms/Button';
import { ButtonMode } from '../../../../../../components/atoms/Button/enums';
import { useCitiesList } from '../../../../../../hooks/useCitiesList';
import routes from '../../../../../../setup/consts/routes';
import { useEntitySelectItems, useFeesProfileData } from '../../OrderForm/hooks';
import {
  currencyOptions,
  missingProfileFeesModalMessages,
} from '../../OrderForm/consts';
import { OrderFormValues } from '../../../types';
import {
  StyledStepGrid,
  StyledStepCard,
  StyledStepCardTitle,
  StyledFeesBanner,
} from '../styles';

const StepStoreCustomer: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const entitySelectItems = useEntitySelectItems();
  const { list: citiesOptions } = useCitiesList();
  const { entityHasNoFeesProfile } = useFeesProfileData();
  const {
    values: { entity, customerId, products = [] },
  } = useFormState<OrderFormValues>();

  return (
    <StyledStepGrid>
      <StyledStepCard>
        <StyledStepCardTitle>
          <FormattedMessage
            id='orders.orderDetails'
            defaultMessage='Order Details'
          />
        </StyledStepCardTitle>
        <FormSimpleSelect
          name='entity'
          label={intl.formatMessage({
            id: 'forms.fields.entity',
            defaultMessage: 'Entity',
          })}
          options={entitySelectItems}
        />
        {entityHasNoFeesProfile && (
          <StyledFeesBanner role='alert'>
            <FormattedMessage
              id='orderWizard.noFeesProfile'
              defaultMessage="This store has no fees profile, so orders can't be placed for it. Contact Sandoog to set it up, or pick a different store."
            />
            <Button
              type='button'
              mode={ButtonMode.Secondary}
              onClick={() => history.push(routes.CONTACT_US)}
            >
              {intl.formatMessage(
                missingProfileFeesModalMessages.confirmButtonLabel
              )}
            </Button>
          </StyledFeesBanner>
        )}
        <FormSimpleSelect
          name='currency'
          label={intl.formatMessage({
            id: 'forms.fields.currency',
            defaultMessage: 'Currency',
          })}
          options={currencyOptions}
          disabled={!entity || !!customerId || products.length > 0}
        />
      </StyledStepCard>
      <StyledStepCard>
        <StyledStepCardTitle>
          <FormattedMessage
            id='orders.customerInformation'
            defaultMessage='Customer information'
          />
        </StyledStepCardTitle>
        <FormInput
          name='name'
          label={intl.formatMessage({
            id: 'forms.fields.customer',
            defaultMessage: 'Customer',
          })}
        />
        <FormInput
          name='phoneNumber'
          label={intl.formatMessage({
            id: 'forms.fields.phoneNumber',
            defaultMessage: 'Phone Number',
          })}
          type='tel'
        />
        <FormInput
          name='secondPhoneNumber'
          label={intl.formatMessage({
            id: 'forms.fields.secondPhoneNumber',
            defaultMessage: 'Second Phone Number',
          })}
          type='tel'
        />
        <FormInput
          name='externalReference'
          label={intl.formatMessage({
            id: 'forms.fields.receiptNumber',
            defaultMessage: 'Receipt Number',
          })}
          type='text'
        />
        <FormSimpleSelect
          disabled={!entity || entityHasNoFeesProfile}
          name='city'
          label={intl.formatMessage({
            id: 'forms.fields.city',
            defaultMessage: 'City',
          })}
          options={citiesOptions}
        />
        <FormInput
          name='address'
          label={intl.formatMessage({
            id: 'forms.fields.address',
            defaultMessage: 'Address',
          })}
          placeholder={intl.formatMessage({
            id: 'forms.placeholders.typeAddress',
            defaultMessage: 'Type address',
          })}
        />
        <FormInput
          name='notes'
          label={intl.formatMessage({
            id: 'forms.fields.addressNotes',
            defaultMessage: 'Address Notes',
          })}
        />
      </StyledStepCard>
    </StyledStepGrid>
  );
};

export default StepStoreCustomer;
